import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { AccountNumberTextInput } from "cerulean";
import {
  CollapsibleCard,
  ResponsiveFlex,
  Button,
  Row,
  Select,
  Alert,
} from "@narmi/design_system";
import * as formAdapters from "../../src/adapters";
import SectionHeader from "./SectionHeader";
import RoutingNumberInput from "./RoutingNumberInput";
import { useDrawerContext } from "./DrawerValidationsProvider";
import { SectionProps } from ".";

const ACHDestinationSection = ({
  title,
  optionalLabel,
  isOpen,
  onClose,
  onOpen,
  onSave,
  recipientId,
  customAlertCopy,
  needsReview,
  achInitialValues,
}: SectionProps) => {
  const { l10n } = useLocalization();
  const accountTypes = entities.recipients.useAccountTypeOptions();
  const recipient = entities.recipients.useRecipient(recipientId);
  const { form, handleSubmit, loading } =
    entities.recipients.useACHDestinationForm(recipientId, achInitialValues);
  const { triggerValidations, validNotSavedSections } = useDrawerContext();
  const [showCancelButton, setShowCancelButton] = useState(false);

  useEffect(() => {
    if (recipient?.ach_destination?.id) {
      setShowCancelButton(true);
    }
  }, []);

  const handleClickSave = () => {
    const onSuccess = () => {
      setShowCancelButton(true);
      // we have to "reset" the form so the form reset's dirty value after being saved
      form.resetForm({ values: form.values });
      onSave();
    };
    handleSubmit(onSuccess);
  };

  const handleClickOpen = () => {
    const { isValid: isDrawerValid } = triggerValidations();
    if (!isDrawerValid) {
      return;
    }
    onOpen();
  };

  const handleClickClose = () => {
    const { isValid: isDrawerValid } = triggerValidations();
    if (!isDrawerValid) {
      return;
    }
    onClose();
  };

  const handleClickCancel = () => {
    form.resetForm();
    onClose();
  };

  return (
    <CollapsibleCard
      onClose={handleClickClose}
      onOpen={handleClickOpen}
      isOpen={isOpen}
      title={title}
      trigger="header"
      renderTitle={() => (
        <SectionHeader
          title={title || "ACH"}
          optionalLabel={optionalLabel}
          isOpen={isOpen}
          checkCondition={Boolean(form.values.accountNumber)}
          {...(needsReview && !recipient?.ach_destination
            ? {
                badgeLabel: l10n.getString(
                  "recipient-drawer-section-needs-review"
                ),
              }
            : {})}
        />
      )}
    >
      <ResponsiveFlex gapSize="s">
        {customAlertCopy ? (
          <Alert
            kind="warn"
            isActive={!!customAlertCopy}
            icon="alert-triangle"
            isDismissable={false}
          >
            <div className="fontSize--s">{customAlertCopy}</div>
          </Alert>
        ) : null}
        {validNotSavedSections.ach ? (
          <div className="message-content negative">
            <span className="narmi-icon-info margin--right--m" />
            Save details to continue.
          </div>
        ) : null}
        <AccountNumberTextInput
          label={l10n.getString("labelAccountNumber", null, "Account number")}
          {...formAdapters.fieldWithOnChange(form, "accountNumber")}
        />

        <RoutingNumberInput
          label={l10n.getString("label-routing", null, "Routing number")}
          routingType="ach"
          {...formAdapters.fieldWithOnChange(form, "routingNumber")}
        />

        <Select
          label={l10n.getString(
            "ach-payment-account-type",
            null,
            "Account type"
          )}
          {...formAdapters.select(form, "accountType")}
        >
          {accountTypes.map((option, index) => (
            <Select.Item key={`account_type_${index}`} value={option}>
              {option}
            </Select.Item>
          ))}
        </Select>
        <Row alignItems="center">
          <Row.Item>
            {showCancelButton ? (
              <Button
                kind="negative"
                testId="cancel-ach-destination"
                onClick={handleClickCancel}
              >
                {l10n.getString("button-cancel")}
              </Button>
            ) : null}
          </Row.Item>
          <Row.Item shrink>
            <Button
              kind="primary"
              testId="save-ach-destination"
              onClick={handleClickSave}
              disabled={loading}
              isLoading={loading}
            >
              {l10n.getString("button-save")}
            </Button>
          </Row.Item>
        </Row>
      </ResponsiveFlex>
    </CollapsibleCard>
  );
};

export default ACHDestinationSection;
